import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="table-of-contents"
// Manages table of contents highlighting and progress bars based on video playback
export default class extends Controller {
  static targets = ["section", "progressBar"]
  static outlets = ["video-player"]

  connect() {
    this.currentSection = null
    this.setupSectionData()
    
    // Listen for time updates from video player
    this.boundHandleTimeUpdate = this.handleTimeUpdate.bind(this)
    document.addEventListener('video-player:timeupdate', this.boundHandleTimeUpdate)
  }

  disconnect() {
    if (this.boundHandleTimeUpdate) {
      document.removeEventListener('video-player:timeupdate', this.boundHandleTimeUpdate)
    }
  }

  // Handle section clicks to seek to timestamp
  sectionClicked(event) {
    if (this.interactiveElementClicked(event)) return

    // Prevent the video_player controller's generic timestamp click handler
    // from also firing (which would seek back to the section start)
    event.preventDefault()
    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation()
    } else if (event.stopPropagation) {
      event.stopPropagation()
    }
    const sectionElement = event.currentTarget
    const timestamp = sectionElement.dataset.timestamp
    if (!this.hasVideoPlayerOutlet || !timestamp) return

    // If clicking the currently active section, toggle play/pause instead of restarting
    if (this.currentSection && this.currentSection.element === sectionElement) {
      try {
        this.videoPlayerOutlet.togglePlayPause()
      } catch (_) { /* swallow */ }
      return
    }

    const seconds = this.timestampToSeconds(timestamp)
    try {
      if (this.videoPlayerOutlet.isVideoPlaying()) {
        // Seek without interrupting play state
        this.videoPlayerOutlet.seekTo(seconds)
      } else {
        // Start playback if currently paused
        this.videoPlayerOutlet.seekAndPlay(seconds)
      }
    } catch (_) { /* swallow */ }
  }

  interactiveElementClicked(event) {
    return event?.target?.closest("a,button,form,label,input,select,textarea")
  }

  setupSectionData() {
    // Parse section data from targets
    this.sections = this.sectionTargets.map((target, index) => {
      const timestamp = target.dataset.timestamp
      const duration = target.dataset.duration
      const startTime = this.timestampToSeconds(timestamp)
      const durationInSeconds = duration ? this.timestampToSeconds(duration) : 0
      const progressBar = this.progressBarTargets[index]
      
      return {
        element: target,
        progressBar: progressBar,
        timestamp: timestamp,
        startTime: startTime,
        duration: durationInSeconds,
        title: target.dataset.title || ''
      }
    }).sort((a, b) => a.startTime - b.startTime)
  }

  handleTimeUpdate(event) {
    const currentTime = event.detail.currentTime
    this.highlightCurrentSection(currentTime)
    this.updateProgressBars(currentTime)
  }

  highlightCurrentSection(currentTime) {
    // Find the current section based on time
    let currentSection = null
    
    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i]
      const nextSection = this.sections[i + 1]
      const endTime = nextSection ? nextSection.startTime : section.startTime + section.duration
      
      if (currentTime >= section.startTime && currentTime < endTime) {
        currentSection = section
        break
      }
    }

    // Update highlighting if section changed
    if (currentSection !== this.currentSection) {
      this.updateHighlighting(currentSection)
      this.currentSection = currentSection
    }
  }

  updateProgressBars(currentTime) {
    this.sections.forEach(section => {
      if (!section.progressBar) return
      
      const nextSection = this.sections[this.sections.indexOf(section) + 1]
      const endTime = nextSection ? nextSection.startTime : section.startTime + section.duration
      
      let progress = 0
      if (currentTime >= section.startTime && currentTime < endTime) {
        // Currently playing this section
        progress = ((currentTime - section.startTime) / (endTime - section.startTime)) * 100
      } else if (currentTime >= endTime) {
        // Section is completed
        progress = 100
      }
      // else: section hasn't started yet, progress remains 0
      
      section.progressBar.value = Math.min(Math.max(progress, 0), 100)
    })
  }

  updateHighlighting(activeSection) {
    // Remove current highlighting and active class
    this.sections.forEach(section => {
      section.element.classList.remove('bg-primary/10', 'border-primary/40', 'active')
      section.element.classList.add('border-primary/20')
    })

    // Add highlighting to active section
    if (activeSection) {
      activeSection.element.classList.remove('border-primary/20')
      activeSection.element.classList.add('bg-primary/10', 'border-primary/40', 'active')
      
      // Scroll active chapter into view
      this.scrollChapterIntoView(activeSection.element)
      
      // Dispatch event for other components
      this.dispatch('sectionChanged', { 
        detail: { 
          section: activeSection,
          timestamp: activeSection.timestamp,
          title: activeSection.title
        }
      })
    }
  }

  scrollChapterIntoView(chapterElement) {
    // Find the scrolling container (the flex container with overflow-x-auto)
    const scrollContainer = chapterElement.parentElement
    if (!scrollContainer) return

    // Get positions
    const containerRect = scrollContainer.getBoundingClientRect()
    const chapterRect = chapterElement.getBoundingClientRect()
    
    // Calculate the relative position of the chapter within the container
    const chapterLeft = chapterRect.left - containerRect.left + scrollContainer.scrollLeft
    const chapterWidth = chapterRect.width
    const containerWidth = containerRect.width
    
    // Calculate the desired scroll position to center the chapter
    const desiredScrollLeft = chapterLeft - (containerWidth / 2) + (chapterWidth / 2)
    
    // Smooth scroll to the calculated position
    scrollContainer.scrollTo({
      left: Math.max(0, desiredScrollLeft), // Don't scroll past the beginning
      behavior: 'smooth'
    })
  }

  timestampToSeconds(timestamp) {
    const parts = timestamp.split(':').map(Number)
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2]
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1]
    }
    return 0
  }
}
